import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import React, { Fragment } from 'react';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { modalStore } from 'components/layout/modals/ModalStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { getJobDisplayTitle } from '../openJobsData';
import ReferralSent from './ReferralSent';

const LINKEDIN_PROFILE_REGEX = /linkedin\.com\/in\/[^/?#\s]+/i;
const MAX_COMMENTS_LENGTH = 2000;

const ValidationSchema = Yup.object().shape({
	name: Yup.string().trim().required().label('Name'),
	email: Yup.string().trim().email().required().label('Email'),
	linkedinUrl: Yup.string()
		.trim()
		.required()
		.test('linkedin-profile', 'Enter a valid LinkedIn profile URL', value =>
			LINKEDIN_PROFILE_REGEX.test(value || '')
		)
		.label('Linkedin'),
	comments: Yup.string()
		.trim()
		.max(MAX_COMMENTS_LENGTH)
		.label('Additional comments')
});

const FIELD_ERROR_CODES = {
	referral_blocked: 'email',
	invalid_email: 'email',
	invalid_linkedin: 'linkedinUrl'
};

export default class ReferralCreate extends ModalComponent {
	static isV2 = true;

	render() {
		return (
			<Formik
				initialValues={{ name: '', email: '', linkedinUrl: '', comments: '' }}
				validationSchema={ValidationSchema}
				validateOnBlur={false}
				validateOnChange={false}
				onSubmit={(values, formikHelpers) => {
					this.saving();
					this.create(values, formikHelpers);
				}}>
				{formikBag => (
					<div className="ExternalApplicationModal ReferralCreate">
						<div className="external-application-modal-top">
							<button
								type="button"
								className="external-application-modal-close"
								onClick={() => this.handleClickCancel()}
								aria-label="Close">
								<img src="/ats/icons/close-3-icon.svg" alt="" />
							</button>
						</div>
						<Form>{this.renderForm(formikBag)}</Form>
					</div>
				)}
			</Formik>
		);
	}

	renderForm(formikBag) {
		const jobTitle = getJobDisplayTitle(this.props.job);

		return (
			<Fragment>
				<div className="external-application-modal-title">
					<h2>Refer someone for {jobTitle || 'this role'}</h2>
					<p>Share their details and we’ll invite them to apply.</p>
				</div>
				<div className="external-application-modal-fields">
					{this.renderField(formikBag, { name: 'name', label: 'Name' })}
					{this.renderField(formikBag, {
						name: 'email',
						label: 'Email',
						type: 'email'
					})}
					{this.renderField(formikBag, {
						name: 'linkedinUrl',
						label: 'Linkedin'
					})}
					{this.renderField(formikBag, {
						name: 'comments',
						label: 'Additional comments',
						multiline: true,
						required: false
					})}
				</div>
				<div className="external-application-modal-footer">
					<button
						type="button"
						className="external-application-btn is-secondary"
						onClick={() => this.handleClickCancel()}>
						Cancel
					</button>
					<button
						type="submit"
						className="external-application-btn is-primary"
						disabled={this.state.saving}>
						{this.state.saving ? 'Sending...' : 'Send Referral'}
					</button>
				</div>
			</Fragment>
		);
	}

	renderField(
		formikBag,
		{ name, label, type = 'text', multiline = false, required = true }
	) {
		const error = formikBag.errors[name];
		const inputProps = {
			name,
			value: formikBag.values[name],
			onChange: event => {
				formikBag.handleChange(event);
				if (error) {
					formikBag.setFieldError(name, undefined);
				}
			}
		};

		return (
			<div className={`external-application-field ${error ? 'is-error' : ''}`}>
				<div className="external-application-label">
					<span>{label}</span>
					{required ? <span className="is-required">*</span> : null}
				</div>
				{multiline ? (
					<textarea
						{...inputProps}
						maxLength={MAX_COMMENTS_LENGTH}
						className="external-application-input is-multiline"
					/>
				) : (
					<input
						{...inputProps}
						type={type}
						className="external-application-input"
					/>
				)}
				{error ? <p className="external-application-error">{error}</p> : null}
			</div>
		);
	}

	async create(values, formikHelpers) {
		const { job } = this.props;

		try {
			const result = await jobPositionStore.createReferral(job.id, values);

			if (result.status === 'success') {
				modalStore.openModal(
					{ isV2: true, position: 'center' },
					<ReferralSent job={job} referredName={result.data?.name} />
				);
				return;
			}

			const field = FIELD_ERROR_CODES[result.code];
			if (field) {
				formikHelpers.setFieldError(field, result.message);
			} else {
				notificationStore.pushErrorNotification(
					'Refer',
					result.message || 'An error has occurred. Please try again.'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Refer',
				error?.message || 'An error has occurred. Please try again.'
			);
		}
		this.saving(false);
	}
}
